import { API } from '../../api';
import { Button, Card, Col, Descriptions, Dropdown, PageHeader, Row, Table, Tag } from 'antd';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { DropdownMenu } from '../editableTable/dropdownMenu';
import React, { useEffect, useState } from 'react';

// could move these
const segmentColumnDescriptions = [
  {
    label: 'Segment',
    text: 'the three-character mnemonic for a segment (i.e., a line) within an HL7 message',
  },
  {
    label: 'Description',
    text: 'the description of the segment/what kind of information it represents',
  },
  {
    label: 'Required',
    text: 'whether the segment can be left out of a message or must be present',
  },
  {
    label: 'Repeatable',
    text: 'whether multiple instances of the segment can be present in the HL7 message',
  },
];

const fieldColumnDescriptions = [
  {
    label: 'Field',
    text: 'a field within an HL7 segment',
  },
  {
    label: 'Description',
    text: 'the description of the field/what kind of information it represents',
  },
  {
    label: 'Multiple Values',
    text: 'whether or not the field can support more than one value',
  },
  {
    label: 'Suggested Values',
    text: "values that should be used for a field's value",
  },
  {
    label: 'Required',
    text: 'whether the field can be left out of a message or must be present',
  },
];

const DataGuide = () => {
  const [selectedResourceType, setSelectedResourceType] = useState(null);
  const [resourceTypeOptions, setResourceTypeOptions] = useState([]);
  const [versionOptions, setVersionOptions] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dataGuidelines, setDataGuidelines] = useState([]);

  const columnSort = (a, b) => {
    if (!!a.name && !!b.name) {
      return a.name.localeCompare(b.name);
    }

    return null;
  };

  const getVersionOptions = async () => {
    const { data: versions } = await API.get('/hl7DataGuidelines/versions');
    setVersionOptions(versions);
    setSelectedVersion(versions[0]);
  };

  const getResourceTypesForVersion = async () => {
    if (selectedVersion) {
      const { data: resourceTypes } = await API.get(
        `/hl7DataGuidelines/resourceTypes?version=${selectedVersion}`,
      );
      setResourceTypeOptions(resourceTypes);
      setSelectedResourceType(resourceTypes[0]);
    }
  };

  const getDataGuidelines = async () => {
    if (selectedVersion && selectedResourceType) {
      const { data: guidelines } = await API.get(
        `/hl7DataGuidelines?resourceType=${selectedResourceType}&version=${selectedVersion}`,
      );

      setDataGuidelines(guidelines);
      setLoading(false);
    } else {
      setLoading(false);
    }
  };

  const handleResourceTypeDropdownSelect = resourceType => {
    if (selectedResourceType !== resourceType) {
      setLoading(true);
      setSelectedResourceType(resourceType);
    }
  };

  const handleVersionDropdownSelect = version => {
    if (selectedVersion !== version) {
      setLoading(true);
      setSelectedVersion(version);
    }
  };

  const createBooleanTag = bool => {
    const tagText = String(bool);
    const tagColor = bool ? 'green' : 'red';
    return <Tag color={tagColor}>{tagText}</Tag>;
  };

  useEffect(() => {
    setLoading(true);
    getVersionOptions();
  }, []);

  useEffect(() => {
    setResourceTypeOptions([]);
    setSelectedResourceType(null);
    getResourceTypesForVersion();
  }, [selectedVersion]);

  useEffect(() => {
    getDataGuidelines();
  }, [selectedResourceType]);

  const segmentFilterOptions = dataGuidelines.map(({ name: segmentName }) => {
    return { text: segmentName, value: segmentName.toLowerCase() };
  });

  const expandedRowRender = ({ fields }) => {
    const fieldRows = fields.map(
      ({
        id,
        field,
        description,
        multiple_values: multipleValues,
        suggested_values: suggestedValues,
        required,
      }) => {
        return {
          key: id,
          field,
          description,
          multipleValues,
          suggestedValues,
          required,
        };
      },
    );

    const columns = [
      {
        title: 'Field',
        dataIndex: 'field',
        key: 'field',
      },
      {
        title: 'Description',
        dataIndex: 'description',
        key: 'description',
      },
      {
        title: 'Multiple Values',
        dataIndex: 'multipleValues',
        key: 'multipleValues',
        render: bool => createBooleanTag(bool),
      },
      {
        title: 'Suggested Values',
        dataIndex: 'suggestedValues',
        key: 'suggestedValues',
      },
      {
        title: 'Required',
        dataIndex: 'required',
        key: 'required',
        render: bool => createBooleanTag(bool),
      },
    ];
    return <Table columns={columns} dataSource={fieldRows} pagination={false} />;
  };

  return (
    <>
      <PageHeader
        className="site-page-header"
        onBack={() => window.history.back()}
        title="HL7v2 Data Guide"
      />
      <Row>
        <Col span={24}>
          <Card title="Column Information">
            <Descriptions title="Segments" layout="vertical">
              {segmentColumnDescriptions.map(({ key, label, text }) => (
                <Descriptions.Item key={key} label={label}>
                  {text}
                </Descriptions.Item>
              ))}
            </Descriptions>
            <Descriptions title="Fields" layout="vertical">
              {fieldColumnDescriptions.map(({ key, label, text }) => (
                <Descriptions.Item key={key} label={label}>
                  {text}
                </Descriptions.Item>
              ))}
            </Descriptions>
          </Card>
        </Col>
        <Col style={{ marginTop: 10, marginRight: 10 }}>
          <Dropdown
            trigger={['click']}
            overlay={
              <DropdownMenu
                options={versionOptions}
                handleMenuClick={e => handleVersionDropdownSelect(e.key)}
              />
            }
          >
            <Button data-cy="select-version-data-guide">
              <b>Hl7v2 Version: </b>
              {selectedVersion} <DownOutlined />
            </Button>
          </Dropdown>
        </Col>
        <Col style={{ marginTop: 10 }}>
          <Dropdown
            trigger={['click']}
            overlay={
              <DropdownMenu
                options={resourceTypeOptions}
                handleMenuClick={e => handleResourceTypeDropdownSelect(e.key)}
              />
            }
          >
            <Button data-cy="select-resource-type-data-guide">
              <b>Resource Type: </b>
              {selectedResourceType} <DownOutlined />
            </Button>
          </Dropdown>
        </Col>
      </Row>
      <Table
        style={{ marginTop: 10 }}
        columns={[
          {
            title: 'Segment',
            dataIndex: 'name',
            sorter: columnSort,
            filterSearch: true,
            filters: segmentFilterOptions,
            onFilter: (value, record) => record.name.toLowerCase() === value,
          },
          {
            title: 'Description',
            dataIndex: 'description',
            sorter: columnSort,
          },
          {
            title: 'Repeatable',
            dataIndex: 'repeatable',
            sorter: columnSort,
            render: bool => createBooleanTag(bool),
          },
          {
            title: 'Required',
            dataIndex: 'required',
            sorter: columnSort,
            render: bool => createBooleanTag(bool),
          },
        ]}
        rowKey="id"
        className="hl7DataGuidelines-table"
        pagination={{ pageSize: 10, size: 'small' }}
        size="medium"
        loading={loading}
        dataSource={dataGuidelines}
        expandable={{
          expandedRowRender,
          // eslint-disable-next-line react/no-unstable-nested-components
          expandIcon: ({ expanded, onExpand, record }) => (
            <Button size="small" onClick={e => onExpand(record, e)} type="primary">
              {expanded ? 'Hide' : 'Show'} Fields {expanded ? <UpOutlined /> : <DownOutlined />}
            </Button>
          ),
        }}
      />
    </>
  );
};

export default DataGuide;
